import { Link } from 'react-router';
import React, { useCallback, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { useTileAdminActions } from '@modules-rocco/admin/hooks/use-tile-admin-actions';
import { useLibraryAction } from '~/modules/directory/context/LibraryActionContext';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { AdminTile } from '@rocco/ui/tile-shared/components/AdminTile';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';

interface DirectoryFeaturedTileProps {
    tile: TileViewModel<ProductTileViewModel>;
    /**
     * Whether this tile is currently being navigated to.
     * Computed at the section level from pendingUrl.
     */
    isPending?: boolean;
    /**
     * Whether this product is in the user's design board.
     * Computed at the section level to avoid per-tile Jotai subscriptions.
     */
    isFollowed?: boolean;
}

/**
 * Tall featured tile for the directory product grid.
 * Designed to span 2 rows with "FEATURED" badge.
 * Includes admin actions and impression tracking.
 */
export const DirectoryFeaturedTile = React.memo(
    ({ tile, isPending = false }: DirectoryFeaturedTileProps) => {
        const tileRef = useRef<HTMLDivElement>(null);
        const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
            null,
        );
        const tracker = useTracker();
        const { model } = tile;
        const primaryImage = model.images?.[0];
        const tileActions = useAtomValue(uiContextAtomTileActions);

        const handleMouseEnter = useCallback(() => {
            hoverTimerRef.current = setTimeout(() => {
                tileActions?.trackingConfig?.onHover?.(
                    tileRef as React.RefObject<HTMLElement>,
                    model.id,
                    model.professional?.id,
                );
            }, 500);
        }, [tileActions, model.id, model.professional?.id]);

        const handleMouseLeave = useCallback(() => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = null;
            }
        }, []);

        // Clear hover timer on unmount to prevent stale tracking events
        useEffect(() => {
            return () => {
                if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                }
            };
        }, []);

        const handleClick = () => {
            trackTileMediaDetailClick(tracker, {
                itemID: model.id,
                itemType: 'ProductTile',
                itemName: model.title,
            });
        };

        useLogImpression({
            data: {
                type: 'LibraryItem_Directory',
                itemID: model.id,
                professionalID: model.professional?.id ?? 0,
            },
            ref: tileRef,
        });

        const tileContent = (
            <div
                ref={tileRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={cn(
                    'group relative transition-opacity duration-150',
                    isPending && 'opacity-60',
                )}
            >
                <Link
                    to={model.link}
                    prefetch="intent"
                    onClick={handleClick}
                    className="gap-3.75 flex flex-col"
                    data-product-tile
                >
                    {/* Image container with intrinsic height via padding-bottom */}
                    {/* Height = 2 × width (matching 2 aspect-square tiles) + 9rem gap */}
                    <span
                        className={cn(
                            'relative block w-full overflow-hidden rounded-lg',
                            'bg-[#FAFAFA]',
                        )}
                    >
                        {/* Spacer creates intrinsic height based on width percentage */}
                        <span
                            className="block"
                            style={{ paddingBottom: 'calc(200% + 9rem)' }}
                        />
                        <span className="absolute inset-0 overflow-hidden">
                            {primaryImage && (
                                <Image
                                    src={primaryImage.src}
                                    alt={primaryImage.alt || model.title}
                                    fill
                                    fit={
                                        primaryImage.multiplied
                                            ? 'contain'
                                            : 'cover'
                                    }
                                    multiplied={primaryImage.multiplied}
                                    sizes="(min-width: 1921px) 595px, ((min-width: 1024px) and (max-width: 1920px)) 31vw, 100vw"
                                    className="transition-transform duration-300 group-hover:scale-105"
                                />
                            )}

                            {/* FEATURED badge - top-left */}
                            <span className="absolute left-5 top-5 flex h-[1.625rem] items-center justify-center rounded-full bg-[#f2f2f2] px-4">
                                <span className="text-[0.625rem] font-medium uppercase leading-[0.625rem] tracking-[0.05rem] text-[#141414]">
                                    featured
                                </span>
                            </span>

                            {/* Save overlay — Pinterest-style board dropdown + Save/Saved button */}
                            {!tile.hideFollowButton && (
                                <TileOverlayWithFollowSelector
                                    pinId={model.id}
                                    pinType="ProductPin"
                                />
                            )}
                        </span>
                    </span>

                    {/* Product info */}
                    <span className="flex shrink-0 flex-col gap-0.5">
                        <TextTooltip content={model.title}>
                            <span className="line-clamp-1 text-lg font-medium leading-[1.3] tracking-[-0.0225rem] text-[#141414]">
                                {model.title}
                            </span>
                        </TextTooltip>
                        <TextTooltip content={model.professional?.name ?? ''}>
                            <span className="line-clamp-1 text-base font-medium leading-[1.3] tracking-[-0.02rem] text-[#737373]">
                                {model.professional?.name}
                            </span>
                        </TextTooltip>
                    </span>
                </Link>
            </div>
        );

        // Wrap with AdminTile if user has admin permissions
        if (tile.adminStatusInfo) {
            return (
                <FeaturedAdminTileWrapper tile={tile}>
                    {tileContent}
                </FeaturedAdminTileWrapper>
            );
        }

        return tileContent;
    },
);

DirectoryFeaturedTile.displayName = 'DirectoryFeaturedTile';

/**
 * Separate component for admin tile wrapping.
 * Isolates useTileAdminActions (which calls useFetcher)
 * so it only runs for admin users.
 */
const FeaturedAdminTileWrapper = ({
    tile,
    children,
}: {
    tile: TileViewModel<ProductTileViewModel>;
    children: React.ReactNode;
}) => {
    const { removeFromLibrary, removedFromLibraryIds } = useLibraryAction();
    const isFadingOut = removedFromLibraryIds?.has(tile.model.id) ?? false;
    const adminActions = useTileAdminActions({
        item: {
            id: tile.model.id.toString(),
            link: tile.model.link ?? '',
            professionalLink: tile.model.professional?.link ?? '',
            pinned: tile.adminStatusInfo?.pinned,
            featuredBrandPinned: tile.adminStatusInfo?.featuredBrandPinned,
            productsAllowed: tile.adminStatusInfo?.productsAllowed,
        },
        itemType: 'products',
        onRemoveFromLibrary:
            removeFromLibrary && !isFadingOut
                ? () =>
                      removeFromLibrary(
                          'product',
                          tile.model.id,
                          tile.model.title ?? '',
                      )
                : undefined,
    });

    return (
        <AdminTile tile={tile} actions={adminActions}>
            <div
                className={cn(
                    'transition-all duration-500 ease-in-out',
                    isFadingOut && 'pointer-events-none scale-95 opacity-0',
                )}
            >
                {children}
            </div>
        </AdminTile>
    );
};
